import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Card } from '@playerbase/ui';

import { useAuth } from './use-auth.js';

// ---------------------------------------------------------------------------
// AuthLoading
// A minimal centred loading state shown while auth status is resolving.
// ---------------------------------------------------------------------------

export function AuthLoading(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card>
        <Card.Content>
          <p className="text-text-muted text-sm">Loading&hellip;</p>
        </Card.Content>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProtectedRoute
// Requires status === 'authenticated' && memberships.length > 0.
// Loading -> AuthLoading
// Unauthenticated -> /sign-in
// Authenticated but no memberships -> /welcome
// ---------------------------------------------------------------------------

export function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element {
  const { status, memberships } = useAuth();

  if (status === 'loading') return <AuthLoading />;
  if (status === 'unauthenticated') return <Navigate to="/sign-in" replace />;
  if (memberships.length === 0) return <Navigate to="/welcome" replace />;

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// RequireAuthNoMembership
// Requires status === 'authenticated' && memberships.length === 0.
// Loading -> AuthLoading
// No session -> /sign-in
// Has membership -> /
// ---------------------------------------------------------------------------

export function RequireAuthNoMembership({ children }: { children: ReactNode }): JSX.Element {
  const { status, memberships } = useAuth();

  if (status === 'loading') return <AuthLoading />;
  if (status === 'unauthenticated') return <Navigate to="/sign-in" replace />;
  if (memberships.length > 0) return <Navigate to="/" replace />;

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// PublicOnlyRoute
// Requires status === 'unauthenticated'.
// Loading -> AuthLoading
// Authenticated with no memberships -> /welcome
// Authenticated with memberships -> /
// ---------------------------------------------------------------------------

export function PublicOnlyRoute({ children }: { children: ReactNode }): JSX.Element {
  const { status, memberships } = useAuth();

  if (status === 'loading') return <AuthLoading />;
  if (status === 'authenticated') {
    return memberships.length === 0
      ? <Navigate to="/welcome" replace />
      : <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
